import { t } from 'i18next';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sendEventHotjar } from '~/helpers/hotjarHelper';
import { useStateVar } from '~/helpers/useStateVar';
import {
  ArrowRightNew,
  CompanyIcon,
  FailSearchUnit,
  SearchNewIcon,
  StarIcon,
} from '~/icons';
import { apiCall, ApiResps } from '~/providers';
import { colors } from '~/styles/colors';
import {
  ActionContainer,
  ContainerArea,
  ContainerItemSelect,
  ContainerSelect,
  ErrorContainer,
  FavoriteButton,
  ScrollableOptions,
  SearchInput,
  StickyFooter,
  TextInformation,
  TooltipContainer,
} from './styles';
import { Tooltip } from './TooltipGlobal';
import { getUserProfile } from '~/helpers/userProfile';
import { ClientBadge } from './components/ClientBadge';

type Option = {
  UNIT_ID: number;
  UNIT_NAME: string;
  CLIENT_ID: number;
  CLIENT_NAME: string;
  IS_FAVORITE: boolean;
}

interface GlobalSearchProps {
  isMobile?: boolean,
  setOpenSearch?: (isOpen: boolean) => void;
  setUnits?: Dispatch<SetStateAction<{
    UNIT_ID: number;
    UNIT_NAME: string;
    CLIENT_ID: number;
    CLIENT_NAME: string;
    IS_FAVORITE: boolean;
  }[]>>;
}

interface UnitItemProps {
  canManageAllClients: boolean;
  option: Option,
  isMobile: boolean,
  setUnitFavorite: (option: Option) => void;
  setState: (state: { unitIdClicked: number }) => void;
}

export function GlobalSearch({
  isMobile = false,
  setOpenSearch,
  setUnits,
}: Readonly<GlobalSearchProps>): ReactElement {
  const [profile] = useState(getUserProfile);
  const [state, render, setState] = useStateVar(() => {
    const state = {
      unitIdClicked: null as number | null,
      isOpen: false,
      query: '',
      hasError: false,
      listFavorite: [] as ApiResps['/global-search/get-list-units']['favoriteUnits'],
      listUnits: [] as ApiResps['/global-search/get-list-units']['listUnits'],
      isLoading: false,
    };
    return state;
  });
  const history = useHistory();
  const inputRef = useRef<HTMLDivElement>(null);
  const manageAllClients = !!profile.manageAllClients;

  function normalize(str: string): string {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  async function setUnitFavorite(item) {
    try {
      if (state.isLoading) return;
      state.isLoading = true;
      const updatedIsFavorite = !item.IS_FAVORITE;
      await apiCall('/global-search/set-favorit-unit', {
        IS_FAVORITE: updatedIsFavorite,
        UNIT_ID: item.UNIT_ID,
      });
      sendEventHotjar(updatedIsFavorite ? 'celsius-fav-unit-busca-global' : 'celsius-unfav-unit-busca-global');
      const updatedListUnits = state.listUnits.map((unit) => (unit.UNIT_ID === item.UNIT_ID ? { ...unit, IS_FAVORITE: updatedIsFavorite } : unit));

      let updatedListFavorite = [...state.listFavorite];
      const isAlreadyFavorite = state.listFavorite.some((fav) => fav.UNIT_ID === item.UNIT_ID);

      if (updatedIsFavorite && !isAlreadyFavorite) {
        const updatedItem = { ...item, IS_FAVORITE: true };
        updatedListFavorite.push(updatedItem);
      } else if (!updatedIsFavorite && isAlreadyFavorite) {
        updatedListFavorite = updatedListFavorite.filter(
          (fav) => fav.UNIT_ID !== item.UNIT_ID,
        );
      }

      const sortUnits = (a, b) => {
        if (a.IS_FAVORITE !== b.IS_FAVORITE) return a.IS_FAVORITE ? -1 : 1;
        const nameCompare = a.UNIT_NAME.localeCompare(b.UNIT_NAME);
        if (nameCompare !== 0) return nameCompare;
        return a.UNIT_ID - b.UNIT_ID;
      };

      updatedListUnits.sort(sortUnits);
      updatedListFavorite.sort(sortUnits);
      setState({
        listUnits: updatedListUnits,
        listFavorite: updatedListFavorite,
      });
      const text = item.IS_FAVORITE ? t('unidadeDesfavoritadaComSucesso') : t('unidadeFavoritadaComSucesso');
      toast.success(text);
    } catch (err) {
      console.log(err);
      toast.error(t('naoFoiPossivelFavoritar'));
    }
    state.isLoading = false;
  }

  function getLabelUnits() {
    if (filteredOptions.length > 1) return t('unidadesDisponiveis');
    return t('unidadeDisponivel');
  }

  async function getData() {
    try {
      const response = await apiCall('/global-search/get-list-units', {});
      if (setUnits) setUnits(response.listUnits);
      setState({ listFavorite: response.favoriteUnits, listUnits: response.listUnits });
    } catch (err) {
      setState({ hasError: true });
      console.log(err);
    }
  }

  const filteredOptions = state.query === '' ? state.listFavorite : state.listUnits.filter((opt) => normalize(opt.UNIT_NAME).includes(normalize(state.query)));

  useEffect(() => {
    if (!state.unitIdClicked) return;
    redirectPage(state.unitIdClicked);
  }, [state.unitIdClicked]);

  function redirectPage(unitId) {
    history.replace('/');
    setTimeout(() => {
      history.replace(`/analise/unidades/${unitId}`); // this setTimeout exist because when we try to change page on 'analise/unidade/{id}' the page didn't refresh
    }, 0);
    sendEventHotjar('celsius-clicked-busca-global');
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setState({ isOpen: false });
        if (setOpenSearch) setOpenSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    getData();
  }, []);

  function getMessageError() {
    if (state.hasError) {
      return (
        <TextInformation>
          <p>{t('erroAoBuscarUnidades')}</p>
          <span>
            {t('regarregarAPagina')}
          </span>
        </TextInformation>
      );
    }
    return (
      <TextInformation>
        <p>{t('nenhumaUnidadeEncontrada')}</p>
        <span>
          {t('suaPesquisaNaoRetornou')}
          <br />
          {t('queTalTentarOutraBusca')}
        </span>
      </TextInformation>
    );
  }

  return (
    <div data-testId="search-component" style={{ position: 'relative', display: 'inline-block', width: isMobile ? '60%' : '383px' }} ref={inputRef}>
      <SearchInput isMobile={isMobile} onClick={() => setState({ isOpen: true })}>
        <ContainerArea>
          <SearchNewIcon color={state.isOpen ? colors.Blue300 : colors.text['sub-500']} />
          <input
            type="text"
            placeholder={isMobile ? t('buscarUnidade') : t('digiteParaBuscarUnidade')}
            value={state.query}
            onFocus={() => setState({ isOpen: true })}
            onChange={(e) => setState({ query: e.target.value })}
            style={{
              border: 'none',
              outline: 'none',
              fontSize: 12,
              flex: 1,
              backgroundColor: 'transparent',
              width: '100%',
            }}
          />
        </ContainerArea>
      </SearchInput>

      {state.isOpen && (
        <ContainerSelect isMobile={isMobile}>
          {(filteredOptions.length > 0 && state.query !== '') && <p>{t('resultadoBusca')}</p>}
          {(filteredOptions.length > 0 && state.query === '') && <p>{t('unidadesFavoritas')}</p>}
          {
            filteredOptions.length > 0 && (
              <ScrollableOptions>
                {filteredOptions.map((option) => (
                  <UnitItem
                    key={option.UNIT_ID}
                    option={option}
                    setState={setState}
                    isMobile={isMobile}
                    setUnitFavorite={setUnitFavorite}
                    canManageAllClients={manageAllClients}
                  />
                ))}
              </ScrollableOptions>
            )
          }
          {(filteredOptions.length === 0) && (
            <ErrorContainer>
              <FailSearchUnit />
              {getMessageError()}
            </ErrorContainer>
          )}
          {
            (filteredOptions.length > 0 && !state.hasError) && (
              <StickyFooter>
                <p>
                  {filteredOptions.length}
                  {' '}
                  {getLabelUnits()}
                </p>
              </StickyFooter>
            )
          }
        </ContainerSelect>
      )}
    </div>
  );
}

function UnitItem({
  option,
  setState,
  isMobile,
  setUnitFavorite,
  canManageAllClients,
}: Readonly<UnitItemProps>): ReactElement {
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [hoveredFavorite, setHoveredFavorite] = useState<number | null>(null);
  const localRef = useRef<HTMLDivElement>(null);
  const favoriteButtonRef = useRef<HTMLDivElement>(null);
  const isHovered = hoveredId === option.UNIT_ID;

  return (
    <ContainerItemSelect key={option.UNIT_ID}>
      <TooltipContainer
        ref={localRef}
        onMouseEnter={() => setHoveredId(option.UNIT_ID)}
        onMouseLeave={() => setHoveredId(null)}
        style={{ width: isMobile ? undefined : '86%', position: 'relative' }}
        onClick={() => setState({ unitIdClicked: option.UNIT_ID })}
        data-testId="unit-selected-global"
      >
        <div>
          <CompanyIcon />
          {canManageAllClients && (<ClientBadge clientName={option.CLIENT_NAME} />)}
        </div>
        <span>{option.UNIT_NAME}</span>
        <Tooltip
          targetRef={localRef}
          text={option.UNIT_NAME}
          visible={isHovered}
          offset={{ top: 4, left: 50 }}
        />
      </TooltipContainer>
      <ActionContainer>
        <FavoriteButton
          ref={favoriteButtonRef}
          isFavorite={option.IS_FAVORITE}
          data-testId={option.IS_FAVORITE ? 'button-unfavorite' : 'button-favorite'}
          onClick={() => setUnitFavorite(option)}
          onMouseEnter={() => setHoveredFavorite(option.UNIT_ID)}
          onMouseLeave={() => setHoveredFavorite(null)}
        >
          <StarIcon />
          <Tooltip
            targetRef={favoriteButtonRef}
            text={option.IS_FAVORITE ? `${t('removerFavorito')}` : `${t('favoritarUnidade')}`}
            visible={hoveredFavorite === option.UNIT_ID}
            offset={{ top: 0, left: -50 }}
            width="auto"
          />
        </FavoriteButton>
        <TooltipContainer
          onMouseEnter={() => setHoveredId(option.UNIT_ID)}
          onMouseLeave={() => setHoveredId(null)}
          onClick={() => setState({ unitIdClicked: option.UNIT_ID })}
          data-testId="unit-selected-global"
        >
          <ArrowRightNew />
        </TooltipContainer>
      </ActionContainer>
    </ContainerItemSelect>
  );
}
